import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import materialInfo from "../Data.js";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";

const Products = () => {
  const navigate = useNavigate();
  const [IsOpen, setIsOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedText, setSelectedText] = useState("");
  const [selectedID, setSelectedID] = useState("");

  const scrollUp = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const showDialog = (image, text, id) => {
    setSelectedImage(image);
    setSelectedText(text);
    setSelectedID(id);
    setIsOpen(true);
    document.body.style.overflow = "hidden";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const hide = () => {
    setIsOpen(false);
    document.body.style.overflow = "visible";
  };

  const product = materialInfo.find((p) => p.id === selectedID);

  const nextImage = () => {
    const currentIndex = product.image.findIndex((img) => img.img === selectedImage);
    const nextIndex = (currentIndex + 1) % product.image.length;
    setSelectedImage(product.image[nextIndex].img);
    setSelectedText(product.image[nextIndex].text || "");
  };

  const previousImage = () => {
    const currentIndex = product.image.findIndex((img) => img.img === selectedImage);
    const prevIndex = (currentIndex - 1 + product.image.length) % product.image.length;
    setSelectedImage(product.image[prevIndex].img);
    setSelectedText(product.image[prevIndex].text || "");
  };

  const [mobile, setMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => { document.body.style.overflow = "visible"; };
  }, []);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Container>
          {/* Page header */}
          <PageHead>
            <PageTag>Product Catalog</PageTag>
            <PageTitle>Our Product Lines</PageTitle>
            <PageSub>
              Premium plastic packaging films for every packaging need.
              All products available with custom printing.
            </PageSub>
          </PageHead>

          {/* Quick nav */}
          <QuickNav>
            {materialInfo.map((i) => (
              <Link to={`/product/${i.id}`} key={i.id}>
                <NavChip>{i.name} &mdash; {i.fullform}</NavChip>
              </Link>
            ))}
          </QuickNav>

          {/* Product grid */}
          <ProductGrid>
            {materialInfo.map((i) => (
              <ProductCard key={i.id}>
                <CardImages>
                  <Carousel showThumbs={false} showStatus={false}>
                    {i.image.map((x) => (
                      <div
                        key={x.ind}
                        onClick={() => showDialog(x.img, x.text, i.id)}
                        style={{ cursor: "zoom-in" }}
                      >
                        <img src={x.img} alt={i.name} />
                      </div>
                    ))}
                  </Carousel>
                </CardImages>
                <CardBody>
                  <CardMeta>
                    <CardName>{i.name}</CardName>
                    <CardFull>{i.fullform}</CardFull>
                  </CardMeta>
                  <CardSpecRow>
                    <CardSpec><strong>Usage:</strong> {i.usage}</CardSpec>
                    <CardSpec><strong>Pattern:</strong> {i.pattern}</CardSpec>
                    <CardSpec><strong>Thickness:</strong> {i.thickness}</CardSpec>
                  </CardSpecRow>
                  <CardActions>
                    <ViewBtn onClick={scrollUp} to={`/product/${i.id}`}>
                      Full Details &rarr;
                    </ViewBtn>
                  </CardActions>
                </CardBody>
              </ProductCard>
            ))}
          </ProductGrid>
        </Container>

        {/* Lightbox */}
        {IsOpen && (
          <Lightbox>
            <CloseBtn onClick={hide}>×</CloseBtn>
            <LightboxInner>
              {mobile ? (
                <>
                  <LightboxImg src={selectedImage} alt="" />
                  <NavRow>
                    {product.image.length > 1 && <ArrowBtn onClick={previousImage}>‹</ArrowBtn>}
                    {product.image.length > 1 && <ArrowBtn onClick={nextImage}>›</ArrowBtn>}
                  </NavRow>
                </>
              ) : (
                <NavRow>
                  {product.image.length > 1 && <ArrowBtn onClick={previousImage}>‹</ArrowBtn>}
                  <motion.div
                    key={selectedImage}
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.25 }}
                  >
                    <LightboxImg src={selectedImage} alt="" />
                  </motion.div>
                  {product.image.length > 1 && <ArrowBtn onClick={nextImage}>›</ArrowBtn>}
                </NavRow>
              )}
            </LightboxInner>
            {selectedText && <LightboxCaption>{selectedText}</LightboxCaption>}
            <LightboxCTA onClick={() => {
              document.body.style.overflow = "visible";
              setIsOpen(false);
              scrollUp();
              navigate(`/product/${selectedID}`);
            }}>
              View Full Details &rarr;
            </LightboxCTA>
          </Lightbox>
        )}
      </motion.div>
    </>
  );
};

/* ─── Styled Components ──────────────────────── */
const Container = styled.div`
  padding: calc(var(--navbar-h) + 64px) 80px 80px;

  @media (max-width: 768px) {
    padding: calc(var(--navbar-h) + 32px) 24px 48px;
  }
`;

const PageHead = styled.div`
  border-bottom: 1px solid var(--color-border);
  padding-bottom: 40px;
  margin-bottom: 40px;
`;

const PageTag = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: var(--color-muted);
  margin-bottom: 12px;
`;

const PageTitle = styled.h1`
  font-size: 48px;
  font-weight: 800;
  color: var(--color-dark);
  margin: 0 0 12px;
  letter-spacing: -1.5px;
`;

const PageSub = styled.p`
  font-size: 16px;
  color: var(--color-muted);
  margin: 0;
  max-width: 520px;
`;

const QuickNav = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 48px;

  a { text-decoration: none; }
`;

const NavChip = styled.span`
  display: inline-block;
  padding: 8px 18px;
  border: 1px solid var(--color-border);
  border-radius: 2px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-muted);
  letter-spacing: 0.5px;
  transition: var(--transition);
  cursor: pointer;
  text-transform: uppercase;

  &:hover {
    border-color: var(--color-dark);
    color: var(--color-dark);
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  overflow: hidden;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled.div`
  background: var(--color-bg);
  display: flex;
  border-right: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);

  &:nth-child(2n) { border-right: none; }
  &:nth-last-child(-n+2) { border-bottom: none; }

  @media (max-width: 768px) {
    flex-direction: column;
    border-right: none;
    &:last-child { border-bottom: none; }
  }
`;

const CardImages = styled.div`
  width: 280px;
  flex-shrink: 0;
  border-right: 1px solid var(--color-border);
  background: var(--color-bg-subtle);
  overflow: hidden;

  .carousel, .carousel-root, .slider-wrapper, .slider, .slide { height: 100%; }

  img {
    width: 100%;
    height: 280px;
    object-fit: cover;
    display: block;
    cursor: zoom-in;
  }

  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid var(--color-border);
    img { height: 220px; }
  }
`;

const CardBody = styled.div`
  flex: 1;
  padding: 32px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) { padding: 24px; }
`;

const CardMeta = styled.div`
  margin-bottom: 20px;
`;

const CardName = styled.h2`
  font-size: 32px;
  font-weight: 900;
  color: var(--color-dark);
  margin: 0 0 4px;
  letter-spacing: -1px;
`;

const CardFull = styled.p`
  font-size: 13px;
  color: var(--color-muted);
  margin: 0;
`;

const CardSpecRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 28px;
  flex: 1;
`;

const CardSpec = styled.p`
  font-size: 13px;
  color: var(--color-muted);
  margin: 0;

  strong {
    color: var(--color-dark);
    font-weight: 600;
    margin-right: 4px;
  }
`;

const CardActions = styled.div``;

const ViewBtn = styled(Link)`
  display: inline-block;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
  color: var(--color-dark);
  border-bottom: 2px solid var(--color-dark);
  padding-bottom: 2px;
  letter-spacing: 0.5px;
  transition: var(--transition);

  &:hover {
    color: var(--color-brand);
    border-color: var(--color-brand);
  }
`;

/* Lightbox */
const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(8, 8, 8, 0.92);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
`;

const LightboxInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseBtn = styled.button`
  position: fixed;
  top: 24px;
  right: 32px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  color: white;
  font-size: 28px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition);
  line-height: 1;
  font-family: var(--font-primary);

  &:hover { background: rgba(255,255,255,0.18); }
`;

const LightboxImg = styled.img`
  width: 440px;
  height: 440px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.5);

  @media (max-width: 768px) {
    width: 280px;
    height: 280px;
  }
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const ArrowBtn = styled.button`
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  color: white;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  font-size: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: var(--transition);
  flex-shrink: 0;
  font-family: var(--font-primary);

  &:hover { background: rgba(255,255,255,0.18); }
`;

const LightboxCaption = styled.p`
  color: rgba(255,255,255,0.5);
  font-size: 13px;
  text-align: center;
  margin: 20px 0 16px;
  letter-spacing: 0.3px;
`;

const LightboxCTA = styled.button`
  display: inline-block;
  background: none;
  border: none;
  border-bottom: 2px solid var(--color-accent);
  font-size: 13px;
  font-weight: 700;
  color: white;
  padding: 0 0 2px;
  letter-spacing: 0.5px;
  font-family: var(--font-primary);
  cursor: pointer;
  transition: var(--transition);

  &:hover { color: var(--color-accent); }
`;

export default Products;
